// Frontend error reporter — captures globals + React crashes + console.error,
// batches and POSTs to /api/debug/log, with localStorage backup.
//
// Safe-by-design: every entrypoint is wrapped in try/catch, the reporter must
// never throw or trigger another report (avoid loops).

import { axiosInstance } from "./axios.js";

const LS_KEY = "sayhi:errors";
const LS_MAX = 50;
const FLUSH_INTERVAL_MS = 4000;
const MAX_BATCH = 20;

let queue = [];
let installed = false;
let originalConsoleError = null;
let isFlushing = false;
let context = {}; // userId, tgId, etc. — set by setReporterContext()

const safe = (fn) => {
  try {
    fn();
  } catch (_) {
    // never throw out of reporter
  }
};

const readBackup = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeBackup = (events) => {
  try {
    const trimmed = events.slice(-LS_MAX);
    localStorage.setItem(LS_KEY, JSON.stringify(trimmed));
  } catch {
    // quota / private mode etc.
  }
};

const enqueue = (event) => {
  safe(() => {
    const enriched = {
      ts: new Date().toISOString(),
      url: window.location.href,
      ua: navigator.userAgent,
      ...context,
      ...event,
    };
    queue.push(enriched);
    const all = readBackup();
    all.push(enriched);
    writeBackup(all);
  });
};

const flush = async () => {
  if (isFlushing || queue.length === 0) return;
  isFlushing = true;
  const batch = queue.splice(0, MAX_BATCH);
  try {
    await axiosInstance.post(
      "/debug/log",
      { events: batch },
      { timeout: 5000 }
    );
  } catch (e) {
    // Put them back for the next attempt.  Never call reporter from here —
    // axios failures get reported by the axios interceptor below; double
    // reporting would loop.
    queue = batch.concat(queue);
  } finally {
    isFlushing = false;
  }
};

export const reportError = (event) => enqueue(event);

export const setReporterContext = (next) => {
  context = { ...context, ...next };
};

export const drainBackup = async () => {
  const buffered = readBackup();
  if (!buffered.length) return;
  try {
    await axiosInstance.post(
      "/debug/log",
      { events: buffered },
      { timeout: 5000 }
    );
    writeBackup([]);
  } catch {
    // keep them for next session
  }
};

export const installErrorReporter = () => {
  if (installed) return;
  installed = true;

  // 1) script errors / runtime errors
  window.addEventListener("error", (ev) => {
    safe(() => {
      // Resource load errors fire on capture phase with target != window.
      if (ev?.target && ev.target !== window && ev.target.tagName) {
        enqueue({
          kind: "resource",
          message: `Failed to load ${ev.target.tagName}`,
          extra: {
            src: ev.target.src || ev.target.href || "",
            tag: ev.target.tagName,
          },
        });
        return;
      }
      enqueue({
        kind: "error",
        message: String(ev?.message || "unknown error"),
        stack: ev?.error?.stack || "",
        extra: {
          filename: ev?.filename,
          lineno: ev?.lineno,
          colno: ev?.colno,
        },
      });
    });
  }, true /* capture, so resource errors come through */);

  // 2) unhandled promise rejections
  window.addEventListener("unhandledrejection", (ev) => {
    safe(() => {
      const reason = ev?.reason;
      enqueue({
        kind: "unhandledrejection",
        message:
          (reason && (reason.message || String(reason))) ||
          "unhandled rejection",
        stack: reason?.stack || "",
      });
    });
  });

  // 3) wrap console.error (preserve original)
  originalConsoleError = console.error.bind(console);
  console.error = (...args) => {
    safe(() => {
      const message = args
        .map((a) => {
          if (a instanceof Error) return a.message;
          if (typeof a === "string") return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(" ");
      const stack = args.find((a) => a instanceof Error)?.stack;
      enqueue({ kind: "console.error", message, stack: stack || "" });
    });
    originalConsoleError(...args);
  };

  // 4) axios interceptor — surface failed API calls
  axiosInstance.interceptors.response.use(
    (r) => r,
    (error) => {
      safe(() => {
        // skip our own /debug/log calls so we never loop
        const url = error?.config?.url || "";
        if (url.includes("/debug/log")) return Promise.reject(error);
        enqueue({
          kind: "axios",
          message: `${error?.config?.method?.toUpperCase() || "REQ"} ${
            url
          } → ${error?.response?.status || "network"}`,
          stack: error?.stack || "",
          extra: {
            status: error?.response?.status,
            data:
              typeof error?.response?.data === "string"
                ? error.response.data.slice(0, 500)
                : error?.response?.data,
          },
        });
      });
      return Promise.reject(error);
    }
  );

  // 5) periodic flush + on unload
  setInterval(() => {
    flush();
  }, FLUSH_INTERVAL_MS);

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });

  // 6) drain any buffered events from previous session
  drainBackup();
};
